import { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Edit, Trash2, Search, Filter, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    role: "staff",
    full_name: "",
    technician_type: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const submitData = {
        email: formData.email,
        role: formData.role,
        full_name: formData.full_name,
        technician_type:
          formData.role === "tech" ? formData.technician_type : null,
      };

      if (editingUser) {
        if (formData.password) {
          submitData.password = formData.password;
        }
        await axios.put(`${API}/users/${editingUser.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("User updated successfully");
      } else {
        submitData.password = formData.password;
        await axios.post(`${API}/users`, submitData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("User created successfully");
      }
      setOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save user");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: "",
      role: user.role,
      full_name: user.full_name,
      technician_type: user.technician_type || "",
    });
    setOpen(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      email: "",
      password: "",
      role: "staff",
      full_name: "",
      technician_type: "",
    });
  };

  const getRoleBadge = (role) => {
    const colors = {
      super_admin: "bg-purple-100 text-purple-700 border-purple-300",
      admin: "bg-indigo-100 text-indigo-700 border-indigo-300",
      agent: "bg-teal-100 text-teal-700 border-teal-300",
      manager: "bg-pink-100 text-pink-700 border-pink-300",
      staff: "bg-emerald-100 text-emerald-700 border-emerald-300",
      tech: "bg-amber-100 text-amber-700 border-amber-300",
    };
    return (
      <span
        className={`px-3 py-1 text-xs font-semibold border rounded-full uppercase ${colors[role] || colors.staff}`}
      >
        {role?.replace("_", " ")}
      </span>
    );
  };

  const getTechTypeBadge = (type) => {
    if (!type) return null;
    const colors = {
      repair: "bg-orange-100 text-orange-700",
      installation: "bg-blue-100 text-blue-700",
      both: "bg-green-100 text-green-700",
    };
    return (
      <span
        className={`ml-2 px-2 py-0.5 text-xs font-medium rounded ${colors[type] || colors.both}`}
      >
        {type}
      </span>
    );
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesRole = roleFilter === "all" || user.role === roleFilter;

    return matchesSearch && matchesRole;
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          Loading...
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">
              User Management
            </h1>
            <p className="text-sm text-slate-600">
              Manage system users and their access roles
            </p>
          </div>
          <Dialog
            open={open}
            onOpenChange={(val) => {
              setOpen(val);
              if (!val) resetForm();
            }}
          >
            <DialogTrigger asChild>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2 rounded-lg shadow-lg shadow-blue-600/30"
                data-testid="add-user-btn"
              >
                <Plus className="w-4 h-4" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden">
              <DialogHeader className="top-0 z-20 bg-white px-6 py-4 border-b">
                <DialogTitle className="text-xl font-bold">
                  {editingUser ? "Edit User" : "Add New User"}
                </DialogTitle>
              </DialogHeader>
              <div className="px-6 pb-6">
                <form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                  data-testid="user-form"
                >
                  <div className="relative">
                    {loading && (
                      <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
                        <div className="flex items-center gap-2 text-blue-700 font-semibold">
                          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          Saving user...
                        </div>
                      </div>
                    )}

                    <div>
                      <Label>Full Name</Label>
                      <Input
                        data-testid="user-name-input"
                        value={formData.full_name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            full_name: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        data-testid="user-email-input"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label>
                        Password{" "}
                        {editingUser && "(leave blank to keep current)"}
                      </Label>
                      <Input
                        type={showPassword ? "text" : "password"}
                        data-testid="user-password-input"
                        value={formData.password}
                        onChange={(e) =>
                          setFormData({ ...formData, password: e.target.value })
                        }
                        required={!editingUser}
                        placeholder={
                          editingUser
                            ? "Leave blank to keep current"
                            : "Default: client123"
                        }
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 mt-[6px] cursor-pointer"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="w-8 h-8" />
                        ) : (
                          <Eye className="w-8 h-8" />
                        )}
                      </button>
                    </div>
                    <div>
                      <Label>Role</Label>
                      <Select
                        value={formData.role}
                        onValueChange={(val) =>
                          setFormData({
                            ...formData,
                            role: val,
                            technician_type: val === "tech" ? "both" : "",
                          })
                        }
                      >
                        <SelectTrigger data-testid="user-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="super_admin">
                            Super Admin
                          </SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="agent">Agent</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="staff">Staff</SelectItem>
                          <SelectItem value="tech">Technician</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Technician Type - only show for tech role */}
                    {formData.role === "tech" && (
                      <div>
                        <Label>Technician Type</Label>
                        <Select
                          value={formData.technician_type}
                          onValueChange={(val) =>
                            setFormData({ ...formData, technician_type: val })
                          }
                        >
                          <SelectTrigger data-testid="user-tech-type-select">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="repair">Repair Only</SelectItem>
                            <SelectItem value="installation">
                              Installation Only
                            </SelectItem>
                            <SelectItem value="both">
                              Repair & Installation
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <Button
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-[20px] font-semibold h-14 mt-4"
                      data-testid="user-submit-btn"
                    >
                      {editingUser ? "Update User" : "Create User"}
                    </Button>
                  </div>
                </form>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search and Filter */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-lg bg-white border-slate-200"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-full sm:w-[180px] rounded-lg bg-white">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="super_admin">Super Admin</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="agent">Agent</SelectItem>
              <SelectItem value="manager">Manager</SelectItem>
              <SelectItem value="staff">Staff</SelectItem>
              <SelectItem value="tech">Technician</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider hidden sm:table-cell">
                    Email
                  </th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider hidden md:table-cell">
                    Created
                  </th>
                  <th className="px-4 sm:px-6 py-4 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-6 py-12 text-center text-slate-500"
                    >
                      {searchQuery || roleFilter !== "all"
                        ? "No users match your filters"
                        : "No users found"}
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr
                      key={user.id}
                      data-testid={`user-row-${user.id}`}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-4 sm:px-6 py-4">
                        <div className="text-sm font-semibold text-slate-900">
                          {user.full_name}
                        </div>
                        <div className="text-xs text-slate-500 sm:hidden">
                          {user.email}
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-sm text-slate-600 hidden sm:table-cell">
                        {user.email}
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-sm">
                        {getRoleBadge(user.role)}
                        {user.role === "tech" &&
                          getTechTypeBadge(user.technician_type)}
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-sm text-slate-600 hidden md:table-cell">
                        {user.created_at
                          ? new Date(user.created_at).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-right">
                        <div className="flex gap-1 sm:gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(user)}
                            className="hover:bg-blue-50 hover:text-blue-600 rounded-lg"
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(user.id)}
                            className="hover:bg-red-50 hover:text-red-600 rounded-lg"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Results count */}
        <div className="mt-4 text-sm text-slate-600">
          Showing {filteredUsers.length} of {users.length} users
        </div>
      </div>
    </AdminLayout>
  );
}
